<template>
  <v-app class="app">
    <v-snackbar
      data-testid="app-notification-toast"
      :model-value="$store.getters.notificationIsActive"
      @update:model-value="$store.dispatch('closeErrorToaster')"
      :timeout="$store.getters.notificationTimeout"
      location="top right"
      :color="$store.getters.notificationColor"
      style="z-index: 100000"
    >
      {{ $store.getters.notificationMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="$store.dispatch('closeErrorToaster')">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!--
      Route-fork PR-R2b: layout switcher.

      `<component :is="currentLayout">` resolves the route's `meta.layout`
      ('superadmin' | 'distributor' | 'portal' | 'storefront' | 'public')
      to a registered layout component, then renders `<router-view />` in
      that layout's default slot.

      Fallbacks:
        - missing meta.layout -> PublicLayout (safest minimum chrome)
        - unknown meta.layout value (e.g., typo) -> PublicLayout

      TheTopBar.vue was deleted in PR-R2b. The breadcrumb + autosave
      indicator chrome moved into each layout shell, fed by the
      `useTopBar()` composable.

      Storefront v-app-bar (shopper chrome) lives inside StorefrontLayout.vue
      and only mounts when a storefront route is active.

      The footer (user info + version) renders for any authenticated
      non-shopper session -- kept here at the App level so it's stable
      across layout transitions and lives in ONE place for every admin
      surface. Its guard (`showVersionFooter`) recognises the Pinia-only
      impersonation session, not just the Vuex account (issue #35).
    -->

    <!--
      Layer 2 of the product-editor duplicate-GET perf fix.

      Previously: `<v-main :key="containerKey">` plus a watcher on
      `containerStyle` that bumped containerKey whenever the computed
      "background: " + getBackgroundColor string changed. Any Vuex
      backgroundColor mutation (or any reactive tick that re-evaluated
      the computed) triggered a full <v-main> remount, which remounted
      <router-view>, which remounted ProductEditor.vue, which re-ran
      onMounted -> another openEditor + loadServerState. Stacked 30+
      deep on a single page load -> 30 identical GETs.

      The :style binding alone updates the background reactively
      without forcing a remount; the :key was structurally unnecessary.
      Dropping it (and the watcher + data slot below) breaks the
      remount feedback loop at its root. Layer 1.v2 in productEditor.js
      contains the symptom even if a future regression reintroduces
      caller-side fan-out.
    -->
    <v-main :style="containerStyle">
      <component :is="currentLayout">
        <router-view></router-view>
      </component>
    </v-main>

    <!--
      Global version footer for admin users.

      `height="32"` rather than `height="auto"`, and the two are NOT
      interchangeable (card mq26K91V, 2026-09-20).

      `app` makes this a fixed layout element, and Vuetify reserves the
      height it MEASURES as `--v-layout-bottom` / `padding-bottom` on
      `.v-main`. With `auto` it measured 31px while the bar actually
      rendered at 32px, because `.app-version-footer`'s
      `min-height: 32px !important` wins after that measurement. The
      reservation was therefore 1px short of the real bar -- so v-main's
      padding was 1px short too, and any child sized against the
      reservation still ran 1px under the bar. Measured: bar 688..720
      (32px tall), `--v-layout-bottom: 31px`.

      Declaring the height tells Vuetify the same number the CSS enforces,
      so the bookkeeping and the rendered box agree and there is ONE
      source for it. The `min-height` rule stays as the floor it always
      was; it simply no longer disagrees with the layout system.
    -->
    <v-footer
      v-if="showVersionFooter"
      app
      class="app-version-footer"
      data-testid="app-version-footer"
      height="32"
    >
      <span class="user-info" data-testid="app-identity-line">
        {{ footerIdentity }}
      </span>
      <v-spacer />
      <!--
        tf-deconetwork STAGE indicator. Renders ONLY on stage; on prod there
        is deliberately nothing here (Jake, 2026-09-24 -- a prod indicator
        would be visible to customers).

        INSIDE the existing status bar rather than as new global chrome,
        which is what keeps it off customer surfaces for free: this footer
        is already gated on `hasLiveSession && !isShopper`, and
        StorefrontLayout clears it outright. A new top-level element would
        have needed its own copy of both rules, and the copy is what
        eventually disagrees.
      -->
      <span
        v-if="showStageBackOfficeChip"
        class="back-office-mode-chip"
        data-testid="app-back-office-mode-indicator"
      >
        Decorator back office: STAGING
      </span>
      <!--
        The brand is a separate text node from the version so the
        white-label case DROPS it rather than rewriting the whole line.
        `versionLabel` is the one place that decides; see its computed.
      -->
      <span
        class="version-info"
        data-testid="app-version-line"
        :data-build-stamp="webBuildStamp"
      >
        {{ versionLabel }}
      </span>
      <!--
        What the serving API said about its release, once it has said it:
        `<N>`, `none` (not a release build) or `unavailable` (the read
        failed). Absent while the request is in flight, so the ui-runner
        scenario can wait for it instead of racing it. Not rendered visibly;
        the number itself is already on the version line.
      -->
      <span
        v-if="apiReleaseMarker"
        hidden
        data-testid="app-api-release"
        :data-release="apiReleaseMarker"
      />
    </v-footer>
  </v-app>
</template>

<script>
import { ref } from "vue";

import axios from "@/axios";
import helpers from "@/mixins/helpers";
import { useAccountStore } from "@/stores/account";
import { useImpersonationStore } from "@/stores/impersonation";

import PublicLayout from "@/layouts/PublicLayout.vue";
import HelpLayout from "@/layouts/HelpLayout.vue";
import SuperAdminLayout from "@/layouts/SuperAdminLayout.vue";
import DistributorLayout from "@/layouts/DistributorLayout.vue";
import PortalLayout from "@/layouts/PortalLayout.vue";
import StorefrontLayout from "@/layouts/StorefrontLayout.vue";
import { isHelpRoute } from "@/router/helpFramed";
// The ONE white-label rule module, shared with the storefront footer, the
// cart footer and the Client Portal. Imported rather than re-implemented
// so this bar cannot disagree with the footer it sits on top of.
import { isWhiteLabelActive } from "@/utils/whiteLabelMark";
import { buildStamp } from "@/utils/buildStamp";
import { fetchApiRelease } from "@/services/apiReleaseService";

// Map of meta.layout value -> registered Vue component name. Anything not
// listed here falls back to PublicLayout (safe minimum chrome).
const LAYOUT_MAP = {
  superadmin: "super-admin-layout",
  distributor: "distributor-layout",
  portal: "portal-layout",
  storefront: "storefront-layout",
  public: "public-layout",
  // Not reachable from a route's `meta.layout` -- help routes keep the
  // meta they inherit from their group, and `currentLayout` overrides it
  // by PATH. Listed here so the map stays the single table of layouts.
  help: "help-layout",
};

export default {
  components: {
    "public-layout": PublicLayout,
    "help-layout": HelpLayout,
    "super-admin-layout": SuperAdminLayout,
    "distributor-layout": DistributorLayout,
    "portal-layout": PortalLayout,
    "storefront-layout": StorefrontLayout,
  },

  // Provide capabilities to DistributorLayout (and any future layout that
  // needs them) without forcing a Pinia setup inside each test mount. The
  // production value comes from the Pinia useAccountStore. We provide an
  // object with a `.value` getter so consumers can unwrap reactively
  // without depending on Pinia/Vue refs directly.
  provide() {
    const self = this;
    return {
      distributorLayoutCapabilities: {
        get value() {
          return self.capabilities;
        },
      },
    };
  },

  mixins: [helpers],

  setup() {
    const accountStore = useAccountStore();
    const impersonationStore = useImpersonationStore();
    // WHICH tf-deconetwork back office the platform is pointed at, or "" when
    // it has not been read (no session, the GET failed, or a value this build
    // does not recognise). "" renders NOTHING -- see `showStageBackOfficeChip`.
    const backOfficeMode = ref("");
    // The serving API's answer about its release number (see
    // services/apiReleaseService.js): `undefined` until it is asked and has
    // answered, `null` when it could not be read, else `{ release }`.
    const apiReleaseAnswer = ref(undefined);
    const apiReleaseRequested = ref(false);
    return {
      accountStore,
      impersonationStore,
      backOfficeMode,
      apiReleaseAnswer,
      apiReleaseRequested,
    };
  },

  computed: {
    capabilities() {
      // Pinia source of truth (PR-11 wired hydrate). Falls back to empty
      // array before login / during logout.
      return this.accountStore?.capabilities ?? [];
    },

    currentLayout() {
      // HELP routes render with minimal chrome, in BOTH presentations.
      //
      // Help is documentation, not an app screen. Jake, on the shipped
      // surface: "when opening a help page in a new tab, we don't need all
      // of the nav links. The intent is to have the stand-alone help page
      // read like a static document."
      //
      // This used to be conditional on `?framed=1`, and the polarity was
      // backwards. FRAMED is the presentation whose parent window already
      // supplies the app chrome, so it needs none -- and it correctly got
      // none. STANDALONE is the one that should read as a bare document,
      // and it fell through to its route group's `meta.layout`, so a help
      // article opened in a new tab arrived wrapped in the entire
      // distributor shell: WORK / REPORTING / ACCOUNT sidebar, top bar,
      // impersonation banner. Exactly the chrome the requirement asks to
      // remove.
      //
      // Keying on the help SUBTREE rather than on the flag makes the rule
      // one statement -- help is minimal chrome -- instead of two that can
      // drift apart. It also means the layout no longer depends on a
      // user-supplied query param at all.
      //
      // `?framed=1` is NOT redundant: `HelpRenderer` still reads it to
      // decide the IN-ARTICLE nav (sidebar list + breadcrumb appear framed,
      // not standalone -- see components/Help/HelpRenderer.vue's
      // presentation rule). This computed simply no longer consults it.
      //
      // LAYOUT ONLY -- NOT AUTH. `meta.layout` has exactly one consumer,
      // this computed. The distributor boundary is enforced by the
      // path-based `requireToken` allowlist plus the per-route
      // `requireCapability('help.access')` guard in router/index.js;
      // neither reads meta. So `/distributor/help/owner/*` stays behind
      // distributor auth and `/help/shopper/*` stays public -- asserted
      // against the real guard in
      // `router/__tests__/help-routes-auth-boundary.spec.js`.
      //
      // WHAT THE READER STILL GETS. HelpLayout, not PublicLayout: a
      // masthead with the ThreadFlow wordmark that links home, and nothing
      // else. Help is not only reached in a new tab -- OnboardingScreen
      // and ReportBuilder `$router.push` into it in the SAME tab -- so a
      // completely bare page would strand a distributor with only the
      // browser Back button. The requirement removes the nav links, not
      // the reader's bearings. See layouts/HelpLayout.vue.
      //
      // Scope: `isHelpRoute` matches only the two help subtrees, so no
      // other surface loses chrome. Locked by
      // `__tests__/help-framed-layout.spec.js` (the decision),
      // `__tests__/help-standalone-no-app-chrome.spec.js` (the DOM), and
      // `layouts/__tests__/help-layout-orientation.spec.js` (the masthead).
      if (isHelpRoute(this.$route)) return LAYOUT_MAP.help;

      const metaLayout = this.$route?.meta?.layout;
      return LAYOUT_MAP[metaLayout] || LAYOUT_MAP.public;
    },

    containerStyle() {
      return "background: " + this.$store.getters.getBackgroundColor;
    },

    userName() {
      return this.$store.state.account?.username || "";
    },

    userEmail() {
      return this.$store.state.account?.email || "";
    },

    // Visibility of the global status bar. It is app-level chrome for any
    // authenticated non-shopper session, on every admin surface (SuperAdmin,
    // Distributor, Client Portal).
    //
    // The legacy `isLoggedIn` getter reads only Vuex `state.account`, which
    // is NOT hydrated during SuperAdmin impersonation (or any page-reload
    // session whose boot accounts/:id fetch 403/404s): router/requireToken.js
    // deliberately hydrates ONLY the Pinia account store on that path because
    // the Vuex `login` action would crash on the null account. That session
    // is still genuinely authenticated -- valid token, hydrated Pinia
    // capabilities -- so gating the footer on Vuex alone made the bar vanish
    // in the Distributor app when reached via impersonation (issue #35).
    //
    // Fall back to the Pinia account store's authenticated signal so the bar
    // shows whenever a live session exists in either store. `!isShopper`
    // stays Vuex-derived: shoppers always have a hydrated Vuex state.account
    // (StorefrontSessionGate requires it), so the shopper exclusion still
    // holds, and the impersonation-null-Vuex case is never a shopper (you
    // cannot impersonate into the storefront surface from the SA flow).
    hasLiveSession() {
      if (this.$store.getters.isLoggedIn) return true;
      // Pinia account store: authenticated when it carries a token or a
      // resolved account id (both hydrated on the impersonation / reload path
      // via requireToken.js -> hydratePiniaFromAuthMe).
      const store = this.accountStore;
      return Boolean(store?.token || store?.accountId != null);
    },

    showVersionFooter() {
      return this.hasLiveSession && !this.$store.getters.isShopper;
    },

    // The tf-deconetwork STAGE chip. TRUE only when the mode is EXACTLY
    // "stage"; everything else -- "prod", an unread value, a failed GET, an
    // unrecognised string -- renders nothing.
    //
    // THE ASYMMETRY IS JAKE'S AND IT IS DELIBERATE (2026-09-24, verbatim):
    // "I want there to be no indicator in the UI that we're on prod since
    // that would be visible to customers, and instead show it in the status
    // bar at the bottom when we're pointed at stage."
    //
    // So this is NOT "display the mode". A prod badge is a defect, not a
    // missing feature, and the absence of a chip is not evidence of stage.
    // It lines up with the risk asymmetry the backend is built around:
    // being on STAGE while you believe you are on PROD loses orders and
    // harms nobody; being on PROD while you believe you are on STAGE makes
    // real garments. The chip reassures that the harmless mode is active,
    // and its absence is deliberately uninformative.
    //
    // Whitelist rather than `!== "prod"`, so a future third mode, a typo in
    // the settings row, or a truncated response cannot render the chip by
    // failing to match "prod".
    showStageBackOfficeChip() {
      return this.backOfficeMode === "stage";
    },

    // The acting user's role name, from the Vuex account. "" when there is
    // no Vuex account -- which is precisely the impersonation / failed-boot
    // session shape described on `hasLiveSession` above.
    //
    // DELIBERATELY NOT read from `localStorage.roleName`. That key is written
    // by the Vuex `login` action only, so it is absent on exactly the
    // sessions that lack `state.account`, and where it IS present it is a
    // login-time SNAPSHOT that a mid-session role change leaves stale. A
    // stale role beside a correct username is worse than no role at all: it
    // reads as authoritative. stores/account.js:19 already records the
    // intent to sweep the remaining `roleName` reads out.
    userRole() {
      return this.$store.state.account?.role?.name || "";
    },

    // WHO the acting user is, on the left of the status bar.
    //
    //   not impersonating:  `Owner, owner_coules (owner@coules.example)`
    //   impersonating:      `<the same> | impersonating Distributor Coules Labs`
    //
    // Jake, 2026-09-23. Two halves, and the ACTING half comes first because
    // it never changes: impersonation is a token STAMP, not a token swap
    // (stores/impersonation.js), so the account performing the actions is the
    // same one throughout. The right half is therefore additional context,
    // not a replacement -- an SA looking at this bar needs to know both whose
    // credentials are acting and whose data is on screen.
    //
    // THE RIGHT HALF NAMES AN ENTITY, NEVER A PERSON. `/impersonate` stamps
    // `tokens.impersonated_distributor_id` and `/impersonate-client` stamps
    // `impersonated_client_id`; there is no impersonated ACCOUNT anywhere in
    // the model to name, so the label is the Distributor or the Client. The
    // role shown is the acting user's own.
    //
    // THE BUG THIS REPLACES: the previous body returned early on `userEmail`,
    // which made the impersonation branch beneath it unreachable for every
    // session that had a Vuex account -- i.e. every SA who logged in
    // normally and then impersonated, which is all of them. The two halves
    // are now composed rather than raced.
    //
    // Either half may be empty: a Pinia-only session has no role/username/
    // email, and a non-impersonating one has no target. Joining the non-empty
    // parts keeps the separator from appearing beside nothing.
    footerIdentity() {
      const parts = [];

      const actor = this.userEmail
        ? `${this.userName || ""} (${this.userEmail})`.trim()
        : "";
      const actorWithRole = [this.userRole, actor].filter(Boolean).join(", ");
      if (actorWithRole) parts.push(actorWithRole);

      const target = this.impersonationTarget;
      if (target) parts.push(`impersonating ${target}`);

      return parts.join(" | ");
    },

    // `Distributor <name>` / `Client <name>` for the active impersonation,
    // "" when none. The entity KIND is always stated: without it "Acme" is
    // ambiguous between a distributor and one of its clients, and the two
    // are different blast radii to be looking at.
    //
    // `mode` is the store's single source of truth for which kind is active
    // (they are mutually exclusive server-side). The name falls back to a
    // generic noun rather than an empty string, because the store's name is a
    // cosmetic localStorage breadcrumb that a cross-device session may not
    // have -- "impersonating Distributor" is still true and still useful.
    impersonationTarget() {
      const store = this.impersonationStore;
      if (!store?.isActive) return "";
      if (store.mode === "client") {
        return `Client ${store.clientName || "(unnamed)"}`.trim();
      }
      return `Distributor ${store.distributorName || "(unnamed)"}`.trim();
    },

    // A PRODUCTION build carries its release number (the prod/r<N> tag it
    // ships as, see tools/change-notes/) as the third component, so the bar
    // reads 1.0.90.<stamp> (Jake, 2026-10-05). Stage and sandbox builds are
    // not releases and keep the package patch: a "90" there would claim a
    // release they are not running.
    //
    // THE SERVING API'S NUMBER WINS. An --api-only release skips the frontend
    // build, so the bundle's VUE_APP_RELEASE names the previous release while
    // a newer API serves. The API reports its own (`/health/version`), read
    // once the bar is shown; the bundle's number is the fallback until that
    // answer arrives, when the read fails, and when the API is not a release
    // build. After a --rollback the API is the previous release, and so is
    // the number shown -- the bar follows what is serving.
    version() {
      const base = process.env.VUE_APP_VERSION || "1.0.0";
      const release = this.releaseNumber;
      if (!release) return base;
      const [major = "1", minor = "0"] = base.split(".");
      return `${major}.${minor}.${release}`;
    },

    // The release number in effect, as a digit string, or "" when this is not
    // a release build. Split out of `version` so `versionStamp` can ask the
    // same question and the two cannot disagree.
    releaseNumber() {
      const fromApi = this.apiReleaseAnswer?.release;
      const release = Number.isInteger(fromApi)
        ? String(fromApi)
        : process.env.VUE_APP_RELEASE;
      return release && /^\d+$/.test(release) ? release : "";
    },

    // The DEPLOY number of a dev or stage build (the `deploy/<server>/<N>`
    // tag its deploy script will record), as a digit string, or "" when this
    // build is not a numbered deploy (sandboxes, production).
    //
    // Same rule as the release number above, for the same reason: THE SERVING
    // API'S NUMBER WINS, because an --api-only deploy skips the frontend
    // build and leaves the bundle's VUE_APP_DEPLOY naming the previous
    // deploy. The bundle's number is the fallback until the API answers, when
    // the read fails, and when the API reports none. Tags start at 1, so 0 and
    // anything non-numeric are "not a deploy number", never rendered.
    deployNumber() {
      const fromApi = this.apiReleaseAnswer?.deploy;
      const deploy =
        Number.isInteger(fromApi) && fromApi > 0
          ? String(fromApi)
          : process.env.VUE_APP_DEPLOY;
      return deploy && /^[1-9]\d*$/.test(deploy) ? deploy : "";
    },

    // What follows the semver in the version line: `YYYYMMDDHHmm` normally,
    // or `YYYYMMDD-<deploy>` on a numbered dev/stage deploy (Jake,
    // 2026-10-19: dev showed `v1.0.0-dev.202610192158`, now
    // `v1.0.0-dev.20261019-26`). The date is the same commit-time stamp, cut
    // to its date: no new time source, no new UTC rule. "" when the commit
    // time is unknown, deploy number or not: absent means absent, and a bare
    // `-26` would read as a stamp we do not have.
    //
    // PRODUCTION IS UNCHANGED. Its release number is already the third
    // component, so a release build keeps the time stamp even if a deploy
    // number somehow reaches it.
    versionStamp() {
      const stamp = this.buildStamp;
      if (!stamp) return "";
      if (this.deployNumber && !this.releaseNumber) {
        return `${stamp.slice(0, 8)}-${this.deployNumber}`;
      }
      return stamp;
    },

    // The COMMIT MOMENT as a padded UTC stamp: `YYYYMMDDHHmm`, 12 digits, no
    // separators, or "" when it is not known. It is appended to the semver as
    // a fourth component, so the whole reads `1.0.0.202609240105`.
    //
    // COMMIT TIME, NOT BUILD TIME (Jake, 2026-09-23). The stamp is a property
    // of the CODE, which makes it unambiguous across deployments: two deploys
    // of the same SHA on the same branch render an identical footer. A build
    // time changes on every rebuild of unchanged source, and a reader
    // comparing this bar against a deploy log cannot tell "the deploy did not
    // take" from "it was rebuilt" when the digits move on their own.
    //
    // THIS REPLACES A LIVE CLOCK, which is the defect and not a nuance. The
    // previous body read `process.env.VUE_APP_BUILD_DATE` with a `new Date()`
    // fallback. That variable is set to EMPTY in every `frontend/.env.*` file
    // and dotenv-webpack defines it as `""` from there, so `build/build.js`'s
    // Node-runtime assignment never reached the bundle and the fallback was
    // constant-folded in. The served SPA read `buildStamp(){const e=new
    // Date,...}` and the digits advanced in the VIEWER'S BROWSER: Jake saw
    // 202609240358 where a peer had seen 202609240330 minutes earlier on the
    // same unchanged container. The input now arrives through
    // build/build-provenance.js, the same DefinePlugin path that already
    // delivers the branch and SHA correctly.
    //
    // UTC, NOT LOCALE. Every reader of this bar is comparing it against a
    // deploy log, a CI run or another machine, all of which speak UTC; a
    // locale string means something different on each reader's screen and
    // cannot be compared or grepped. Hence the UTC getters -- `getUTCMonth`
    // is 0-based, so +1.
    //
    // THE CONVERSION HAPPENS HERE, NOT IN SHELL, and that is deliberate. The
    // value is `git show -s --format=%cI` (ISO-8601 WITH the offset), because
    // `TZ=UTC git show --date=format:...` SILENTLY IGNORES TZ and answers in
    // local time -- for commit 1953e93f6, `202609231805` instead of
    // `202609240105`: seven hours and a different date, and plausible enough
    // to ship. Passing the offset through and letting `getUTC*` do the work
    // keeps one conversion, in the one place that cannot get the zone wrong.
    //
    // ABSENT MEANS ABSENT -- NO FALLBACK TO `now`. An unknown or unparseable
    // commit time renders NOTHING (see `versionLabel`), never a fabricated
    // moment. A plausible-looking wrong timestamp is worse than a visibly
    // missing one: it is indistinguishable from a real one by looking, which
    // is precisely how the live clock survived a green test suite and a
    // review.
    buildStamp() {
      return buildStamp();
    },

    // The settled API answer as a marker value: "" while it is pending, else
    // the release, "none" or "unavailable". See the hidden span it feeds.
    apiReleaseMarker() {
      const answer = this.apiReleaseAnswer;
      if (answer === undefined) return "";
      if (answer === null) return "unavailable";
      return Number.isInteger(answer.release) ? String(answer.release) : "none";
    },

    // `tf-web-build:<sha>:clean|dirty|unknown` (build/build-provenance.js).
    // Rendered as an attribute so DefinePlugin splices the literal into the
    // served bundle, where the selective UI merge gate reads it.
    webBuildStamp() {
      return process.env.VUE_APP_BUILD_STAMP || "";
    },

    // `branch - shortSHA` from the build args, or `local - unstamped`.
    //
    // Jake, 2026-09-23, chose the explicit unstamped wording over an empty
    // parenthetical: a reader who sees `(local - unstamped)` knows the field
    // exists and that this build simply carries no git provenance, whereas a
    // blank looks like something broke.
    //
    // Seven characters of SHA -- git's own default abbreviation, so the value
    // can be pasted straight into `git show`.
    buildProvenance() {
      const branch = process.env.VUE_APP_GIT_BRANCH;
      const commit = process.env.VUE_APP_GIT_COMMIT;
      if (!branch || !commit) return "local - unstamped";
      return `${branch} - ${commit.slice(0, 7)}`;
    },

    // True only while a WHITE-LABELED storefront is on screen.
    //
    // Card https://trello.com/c/mq26K91V. Jake, 2026-09-20: "when using
    // custom branding, we should only show the version number and not
    // 'ThreadFlow'".
    //
    // TWO CONDITIONS, AND BOTH ARE LOAD-BEARING.
    //
    // 1. THE ROUTE. This bar is global -- it renders on admin, distributor,
    //    superadmin and portal surfaces, where no store exists and the
    //    question is meaningless. `storefrontWhiteLabel` is Vuex state that
    //    OUTLIVES a route change (useStorefrontChrome clears it on the next
    //    storefront fetch, not on leaving one), so without this check an
    //    owner who visited their white-labeled store and then opened
    //    /distributor would carry the stripped brand with them. Off a
    //    storefront the line must read ThreadFlow: an absent store is not
    //    permission to strip the brand, it is the absence of permission to.
    //    Same shape as Unit 7's unknown-host rule and as
    //    storefrontBrowserChrome's `isStorefrontRoute` guard, which gates the
    //    browser tab on exactly this signal.
    //
    // 2. THE RESOLVER'S ANSWER. Delegated to `resolveWhiteLabelMark` rather
    //    than re-testing `enabled` / `white_label_on` here. That function is
    //    the ONE rule module -- the storefront footer, the cart footer and
    //    the Client Portal already ask it -- and it requires BOTH the tier
    //    entitlement and the per-store toggle. A second copy of the gate in
    //    this file is exactly how this bar and the footer beneath it would
    //    come to disagree, which is the half-applied white-label the feature
    //    exists to prevent. `isStoreLogo` is deliberately NOT consulted: a
    //    white-labeled store with no logo is still white-labeled.
    //
    // `isWhiteLabelActive` ASKS THE QUESTION DIRECTLY, and the first version
    // of this line did not -- worth recording, because the failure was live
    // for an hour and the tests were green through it.
    //
    // It originally inferred the answer from the RENDER SHAPE:
    // `!resolveWhiteLabelMark(...).isLink`. That was sound while the only
    // href the resolver could emit was threadflow.com, so "is a link" meant
    // "is the ThreadFlow mark". Unit 10 (#3673) then landed
    // `link_url` -- a white-labeled store now links to the DISTRIBUTOR's own
    // site -- so `isLink` became true on white-labeled rows too, and the
    // inference would have reported "not white-labeled" for precisely the
    // stores this feature exists for, restoring ThreadFlow's name to a
    // white-labeled page. The specs did not catch it because their fixture
    // never set `link_url`.
    //
    // A field describing HOW to render is not a stable proxy for WHETHER the
    // feature is on. The predicate carries both gates (tier entitlement AND
    // per-store toggle) and lives in the rule module beside the resolver, so
    // this bar and the footer beneath it still cannot disagree.
    //
    // A null/absent answer reads as false, so an in-flight fetch keeps the
    // brand rather than flashing an unbranded line.
    isWhiteLabeledStorefront() {
      if (this.$route?.meta?.layout !== "storefront") return false;
      return isWhiteLabelActive(this.$store.getters.getStorefrontWhiteLabel);
    },

    // The version line's full text. One computed so the brand-vs-no-brand
    // decision has a single home and the template stays a text binding.
    // `ThreadFlow v1.0.0.202609240105 (main - 1953e93)`, or without the
    // fourth component when no commit time reached the bundle:
    // `ThreadFlow v1.0.0 (local - unstamped)`.
    //
    // NO SEPARATE MARKER FOR THE MISSING STAMP. The stamp now comes from the
    // SAME source as the branch and SHA (build/build-provenance.js), so the
    // three are absent together, always -- and `(local - unstamped)` beside
    // it already tells the reader this build carries no git provenance. A
    // second marker for one condition would be a second mechanism for one
    // concept, and a reader would have to learn which of the two to trust.
    versionLabel() {
      const stamp = this.versionStamp;
      const semver = stamp ? `${this.version}.${stamp}` : this.version;
      const version = `v${semver} (${this.buildProvenance})`;
      return this.isWhiteLabeledStorefront ? version : `ThreadFlow ${version}`;
    },
  },

  mounted() {
    // Re-derive impersonation state from the backend on every app boot. The
    // impersonation store is memory-only and DistributorList.doImpersonate()
    // reloads the page right after start, wiping it -- so without this the
    // ImpersonationBanner (the SA's only "Stop impersonating" exit) never
    // renders in the distributor app. Only probe when a session token
    // exists; an anonymous boot has nothing to hydrate and the status call
    // would 401. hydrate() swallows its own errors, so this is fire-and-forget.
    if (localStorage.getItem("token")) {
      this.impersonationStore.hydrate();
      this.$store.dispatch('setSuppressErrorSnackbars');
    }
  },

  watch: {
    // WATCHED, NOT CALLED ONCE IN mounted(), and the difference is a real
    // bug that unit tests could not see (found by driving sandbox 8086).
    //
    // On a page load App.vue mounts BEFORE the session stores hydrate, so
    // `showVersionFooter` is FALSE at mounted() and a one-shot fetch there
    // returns early and never retries. The status bar then appears a moment
    // later with NO CHIP ON IT -- which is indistinguishable from prod, the
    // dangerous direction of the two. Every component test passed because a
    // test mount supplies the session up front.
    //
    // `immediate` keeps the already-hydrated case (an in-app navigation)
    // working without waiting for a change that will never come.
    showVersionFooter: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.loadBackOfficeMode();
          this.loadApiRelease();
        }
      },
    },
  },

  methods: {
    /**
     * Read the tf-deconetwork back-office mode for the status-bar chip.
     *
     * GATED ON A LIVE SESSION by the watcher above, and that gate is not
     * merely an optimisation. The endpoint is `@login_required`, so an
     * anonymous storefront visitor would take a 401 on every page load --
     * noise in the console on the one surface that is supposed to know
     * nothing about back offices. The guard is re-checked here as well so
     * the method is safe to call directly.
     *
     * SWALLOWS ITS ERRORS AND LEAVES THE MODE UNSET. A failed read must not
     * render a stage chip on an environment that might be prod: the harm
     * here is a customer seeing a staging badge, not an admin missing one.
     */
    /**
     * Ask the serving API for its release number, once per page load.
     *
     * Gated on the bar being shown, like the back-office read above, so a
     * shopper storefront (which has no bar) never makes the call. Never
     * awaited by anything that renders: the line shows the bundle's number
     * at once and changes only if the API reports a different one. The
     * service never rejects, so there is nothing to catch here.
     */
    async loadApiRelease() {
      if (!this.showVersionFooter || this.apiReleaseRequested) return;
      this.apiReleaseRequested = true;
      this.apiReleaseAnswer = await fetchApiRelease(process.env.API_BASE_URL);
    },

    async loadBackOfficeMode() {
      if (!this.showVersionFooter) return;
      try {
        const resp = await axios.get(
          "/api/platform-settings/deconetwork-back-office-mode"
        );
        const value = resp?.data?.value;
        this.backOfficeMode = typeof value === "string" ? value : "";
      } catch (e) {
        this.backOfficeMode = "";
      }
    },
  },
};
</script>

<style>
/*
 * Fixed-viewport root. Clamping html/body/#app/.v-application to the
 * viewport height makes <v-main> (a flex child of .v-application) a
 * height-constrained box rather than a content-grown one, which lets
 * the per-surface layout shells (.dist-layout, .sa-layout, .portal-layout,
 * .storefront-layout) use height: 100% reliably. The document never
 * scrolls; each shell decides which inner region (sidebar, main) gets
 * the scrollbar.
 *
 * The Vuetify app-footer (rendered conditionally for admins in App.vue)
 * still participates in the layout system and reduces the height
 * Vuetify allocates to <v-main>; no manual subtraction needed.
 */
html, body, #app {
  height: 100%;
  overflow: hidden;
  margin: 0;
}
.v-application,
.v-application__wrap {
  height: 100%;
  min-height: 0 !important;
}
.v-main {
  /* Vuetify's default is min-height: 100vh which lets <v-main> grow with
   * content. We need it height-clamped so our layout shells' height: 100%
   * resolves to a fixed value. Setting min-height: 0 disables the
   * growth, and the Vuetify layout system still subtracts the app-footer
   * height via --v-layout-* CSS vars so <v-main> ends up exactly viewport
   * minus footer. */
  min-height: 0 !important;
  height: 100%;
}
.v-main__wrap {
  /* Same clamp on the inner wrap so the shell can fill it. */
  min-height: 0 !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.v-main__wrap > * {
  /* The dynamic <component :is="currentLayout"> rendered into v-main's
   * slot is a block child by default. Without flex: 1, height: 100% on
   * the inner layout shell resolves to "as tall as content" instead of
   * "fill the parent." Force it to fill the flex column. */
  flex: 1;
  min-height: 0;
}

.app {
  background-color: white !important;
}

.storefront-toolbar .v-toolbar__content {
  height: 64px !important;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14),
    0 1px 10px 0 rgba(0, 0, 0, 0.12);
}

.logo-img {
  height: 32px;
  width: auto;
}

.clip {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}

.word-break-all {
  word-break: break-all;
}

.toolbar {
  /* v-app-bar handles positioning via Vuetify 3 layout system */
}

.toolbar-title-top {
  font-size: 24px !important;
  font-weight: normal !important;
  max-width: 65%;
}

/* The tf-deconetwork STAGING chip. Amber rather than red: this is the
 * SAFE state, and a red badge would read as an error condition an admin
 * should fix. It sits between the identity line and the version, inside
 * the 32px bar, so it must not grow the bar's height -- see the
 * height="32" comment on <v-footer>, where a 1px measurement mismatch
 * already caused a layout bug. */
.back-office-mode-chip {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
  padding: 3px 8px;
  margin-right: 12px;
  border-radius: 3px;
  background: #fff3cd;
  color: #7a5b00;
  white-space: nowrap;
}

.app-version-footer {
  background-color: #f5f5f5 !important;
  border-top: 1px solid #e0e0e0;
  min-height: 32px !important;
  padding: 4px 16px !important;
}

.user-info {
  font-size: 0.75rem;
  color: #666;
  opacity: 0.8;
}

.version-info {
  font-size: 0.75rem;
  color: #666;
  opacity: 0.8;
}

.toolbar-title {
  font-size: 24px !important;
  font-weight: normal !important;
  max-width: 65%;
}

.md-subheader {
  margin-top: 48px !important;
  margin-bottom: 16px !important;
  font-size: 22px !important;
  font-weight: medium !important;
}

.toolbar-color {
  background-color: red;
}
</style>
